import { useState } from 'react';
import { Trash2, Pencil, Clock, AlertTriangle, Plus } from 'lucide-react';
import { useVisibleStore } from '../hooks/useVisibleStore';
import { AddPointTaskModal } from './AddPointTaskModal';

export function TasksConfig() {
  const { tasks, shifts, deleteTask, updateTask, categories } = useVisibleStore();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editCategoryId, setEditCategoryId] = useState('');
  const [editRequired, setEditRequired] = useState(1);
  const [editDesc, setEditDesc] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const visibleCats = categories.filter(c => !c.isGeneral);

  const startEdit = (task: any) => {
    setEditingId(task.id);
    setEditName(task.name);
    setEditCategoryId(task.categoryId || '');
    setEditRequired(task.requiredTeam || 1);
    setEditDesc(task.descriptionMarkdown || '');
  };

  const saveEdit = () => {
    if (!editingId || !editName.trim()) return;
    updateTask(editingId, {
      name: editName.trim(),
      categoryId: editCategoryId || undefined,
      requiredTeam: editRequired,
      descriptionMarkdown: editDesc || undefined,
    });
    setEditingId(null);
  };

  const handleDelete = (id: string) => {
    deleteTask(id);
    setDeleteConfirm(null);
  };

  const getShiftCount = (taskId: string) => shifts.filter(s => s.taskId === taskId).length;

  return (
    <div className="h-full overflow-y-auto custom-scrollbar p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Tâches</h2>
          <p className="text-sm text-slate-400 mt-1">Gérez les tâches créées depuis les créneaux</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl text-sm transition-colors shadow-lg"
        >
          <Plus className="w-4 h-4" /> Nouvelle Tâche
        </button>
      </div>

      {/* Tasks list */}
      <div className="space-y-3">
        {tasks.length === 0 ? (
          <div className="text-center py-16 text-slate-500">
            <ClipboardList className="w-12 h-12 mx-auto mb-4 opacity-20" />
            <p className="text-sm">Aucune tâche créée</p>
            <p className="text-xs mt-1">Utilisez le bouton "Point / Tâche" dans les Créneaux pour en créer</p>
          </div>
        ) : (
          tasks.map(task => {
            const isEditing = editingId === task.id;
            const assignedCount = getShiftCount(task.id);
            const cat = task.categoryId ? categories.find(c => c.id === task.categoryId) : null;

            return (
              <div key={task.id} className="bg-[#14161B] border border-[#2D3139] rounded-xl overflow-hidden">
                {isEditing ? (
                  <div className="p-4 space-y-3">
                    <input
                      value={editName}
                      onChange={e => setEditName(e.target.value)}
                      placeholder="Nom de la tâche"
                      className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-3 py-2 text-white text-sm focus:border-indigo-500 focus:outline-none"
                    />
                    <div className="grid grid-cols-2 gap-3">
                      <select value={editCategoryId} onChange={e => setEditCategoryId(e.target.value)} className="bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-3 py-2 text-white text-sm focus:border-indigo-500 focus:outline-none">
                        <option value="">Sans catégorie</option>
                        {visibleCats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                      <input type="number" min={1} value={editRequired} onChange={e => setEditRequired(parseInt(e.target.value)||1)} className="bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-3 py-2 text-white text-sm focus:border-indigo-500 focus:outline-none" />
                    </div>
                    <textarea value={editDesc} onChange={e => setEditDesc(e.target.value)} rows={2} placeholder="Description..." className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-3 py-2 text-white text-sm resize-none focus:border-indigo-500 focus:outline-none" />
                    <div className="flex gap-2">
                      <button onClick={() => setEditingId(null)} className="px-4 py-2 bg-[#1A1D23] border border-[#2D3139] text-slate-400 rounded-lg text-xs font-bold">Annuler</button>
                      <button onClick={saveEdit} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-xs font-bold">Enregistrer</button>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <ClipboardList className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                        <h3 className="font-bold text-white text-sm truncate">{task.name}</h3>
                        {cat && <span className="text-[9px] bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 px-1.5 py-0.5 rounded flex-shrink-0">{cat.name}</span>}
                      </div>
                      <div className="flex items-center gap-3 text-[10px] text-slate-500 mt-1">
                        <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {task.requiredTeam} requis</span>
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {(task.openingHours||[]).length} créneau(x)</span>
                        <span>{assignedCount} team assigné{assignedCount > 1 ? 's' : ''}</span>
                      </div>
                      {task.descriptionMarkdown && <p className="text-[11px] text-slate-400 mt-1.5 line-clamp-1">{task.descriptionMarkdown}</p>}
                    </div>
                    <div className="flex items-center gap-1 ml-3 flex-shrink-0">
                      <button onClick={() => startEdit(task)} className="p-2 hover:bg-[#2D3139] rounded-lg text-slate-400 hover:text-indigo-400" title="Modifier"><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => setDeleteConfirm(task.id)} className="p-2 hover:bg-red-500/20 rounded-lg text-slate-400 hover:text-red-400" title="Supprimer"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[150] flex items-center justify-center p-4">
          <div className="bg-[#14161B] border border-[#2D3139] rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-xl bg-red-500/20"><AlertTriangle className="w-5 h-5 text-red-400" /></div>
              <div>
                <h3 className="font-bold text-white text-sm">Supprimer la tâche ?</h3>
                <p className="text-[10px] text-slate-400">Les shifts associés seront aussi supprimés.</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 bg-[#1A1D23] border border-[#2D3139] text-slate-400 rounded-xl text-sm font-bold">Annuler</button>
              <button onClick={() => handleDelete(deleteConfirm)} className="flex-1 py-2.5 bg-red-600 hover:bg-red-500 text-white rounded-xl text-sm font-bold">Supprimer</button>
            </div>
          </div>
        </div>
      )}

      {/* Add modal */}
      <AddPointTaskModal isOpen={showAddModal} onClose={() => setShowAddModal(false)} />
    </div>
  );
}

// Need this for the icon
function Users({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function ClipboardList({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <rect width="8" height="4" x="8" y="2" rx="1" ry="1" />
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <path d="M12 11h4" /><path d="M12 16h4" /><path d="M8 11h.01" /><path d="M8 16h.01" />
    </svg>
  );
}
